import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../stores/app-store';
import {
  BarChart3,
  TrendingUp,
  Clock,
  Target,
  Flame,
  FileText,
  BookMarked,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { EChartsChart, buildWeeklyBarOption, buildPalette } from './ui/echarts-chart';
import { WeekConstellation } from './dashboard-view';
import { ShootingStars, LevelStarfield } from './moments';
import { Rise } from './ui/rise';
import { InicioSkeleton } from './ui/skeletons';
import { CountUp } from './ui/count-up';
import { BookCover } from './book-cover';
import { LEVEL_XP, totalWordsOf, writerLevelOf, writerTitleOf } from '../lib/writer-level';

/** Constelação que PREENCHE o contêiner: mede largura/altura reais e gera um
    viewBox proporcional — nada de letterbox, nada de distorção. */
function AutoConstellation({
  days,
}: {
  days: { date: string; dayName: string; wordsWritten: number; isToday: boolean }[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [dim, setDim] = useState({ w: 560, h: 160 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      if (r.width > 40 && r.height > 40) {
        setDim({ w: Math.round(r.width), h: Math.round(r.height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="flex-1 min-h-0 max-h-[200px] w-full my-auto [&_.const2]:h-full [&_.const2]:w-full">
      <WeekConstellation days={days} width={dim.w} height={dim.h} fill />
    </div>
  );
}

export function StatsView() {
  const { t } = useTranslation();
  const { getCurrentProject, projects, dailyStats, getWeekStats, projectsLoaded } = useAppStore();
  // Stats is accessible via the global menu; if no book is "open",
  // fall back to the first available book instead of loading forever.
  const project = getCurrentProject() ?? projects[0];

  const stats = useMemo(() => {
    if (!project) return null;

    const chapters = project.chapters || [];
    const sessions = project.sessions || [];

    const totalWords = chapters.reduce((sum, ch) => sum + (ch.wordCount || 0), 0);
    const totalChapters = chapters.length;
    const avgWordsPerChapter = totalChapters > 0 ? Math.round(totalWords / totalChapters) : 0;

    const totalSessions = sessions.length;
    const totalTimeMinutes = sessions.reduce((sum, s) => {
      if (!s.endTime) return sum;
      return sum + (new Date(s.endTime).getTime() - new Date(s.startTime).getTime()) / 1000 / 60;
    }, 0);

    const avgSessionLength = totalSessions > 0 ? Math.round(totalTimeMinutes / totalSessions) : 0;

    // Calculate writing streak
    const sortedDates = dailyStats
      .map((s) => s.date)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    let streak = 0;
    const today = new Date().toISOString().split('T')[0];
    const checkDate = new Date();

    for (const date of sortedDates) {
      const expectedDate = checkDate.toISOString().split('T')[0];
      if (date === expectedDate) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else if (date === today) {
        continue;
      } else {
        break;
      }
    }

    // Best day
    const bestDay = dailyStats.reduce(
      (best, day) => (day.wordsWritten > (best?.wordsWritten || 0) ? day : best),
      dailyStats[0]
    );

    // Today's words
    const todayStats = dailyStats.find((s) => s.date === today);

    return {
      totalWords,
      totalChapters,
      avgWordsPerChapter,
      totalTimeMinutes,
      totalSessions,
      avgSessionLength,
      streak,
      bestDay,
      todayWords: todayStats?.wordsWritten || 0,
    };
  }, [project, dailyStats]);

  const weekStats = getWeekStats();

  // Nível do escritor — cartões "Nv" e "Próximo marco" (mock)
  const writerLvl = writerLevelOf(totalWordsOf(projects));

  // 7 dias — alimenta a constelação e o gráfico semanal
  const constDays = Array.from({ length: 7 }).map((_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    const dateStr = date.toISOString().split('T')[0];
    const dayStat = weekStats.find((d) => d.date === dateStr);
    return {
      date: dateStr,
      dayName: date.toLocaleDateString('pt-BR', { weekday: 'short' }),
      wordsWritten: dayStat?.wordsWritten || 0,
      isToday: i === 6,
    };
  });
  const litStars = constDays.filter((d) => d.wordsWritten > 0).length;

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} ${t('stats.minutes')}`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // ── Primeira carga: a casca fica, os dados pulsam (nunca 'sem dados' no load) ──
  if (!projectsLoaded) {
    return <InicioSkeleton />;
  }

  // ── Empty state — no project ──────────────────────────────────────────────
  if (!project) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-muted/50">
          <BookMarked className="h-8 w-8 text-muted-foreground/50" aria-hidden />
        </div>
        <h2 className="mb-2 font-serif text-xl font-semibold text-foreground">
          {t('stats.noBookTitle', 'Nenhum livro ainda')}
        </h2>
        <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
          {t('stats.noBookDesc', 'Crie seu primeiro livro para começar a ver estatísticas de escrita.')}
        </p>
      </div>
    );
  }

  // ── Empty state — no stats data ──────────────────────────────────────────
  if (!stats) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-muted/50">
          <BarChart3 className="h-8 w-8 text-muted-foreground/50" aria-hidden />
        </div>
        <h2 className="font-serif text-xl font-semibold text-foreground">
          {t('stats.noDataYet')}
        </h2>
      </div>
    );
  }

  return (
    <div className="relative flex-1 min-h-0 overflow-y-auto md:overflow-hidden [-webkit-overflow-scrolling:touch] animate-fade-in">
      <ShootingStars />
      <LevelStarfield level={writerLvl.level} />
      <div className="px-4 py-5 md:px-[30px] md:py-5 w-full md:h-full flex flex-col gap-4 md:gap-3.5">

        {/* ── Header ── */}
        <Rise i={0} className="shrink-0">
          <p className="m-eyebrow">{t('stats.title')}</p>
          <h1 className="font-serif text-[22px] font-medium leading-snug text-foreground mt-1">
            {t('hud.skyTitle', 'O céu da sua escrita.')}
          </h1>
        </Rise>

        {/* ── Fileira do mock — números nunca quebram ── */}
        <Rise i={1} className="grid grid-cols-2 gap-2.5 md:grid-cols-4 md:gap-3.5 shrink-0">
          <div className="dcard min-w-0">
            <p className="dcard-t truncate">{t('stats.totalWords')}</p>
            <p className="ritual-num whitespace-nowrap tabular-nums !text-[clamp(20px,1.8vw,30px)]"><CountUp value={stats.totalWords} /></p>
          </div>
          <div className="dcard min-w-0">
            <p className="dcard-t truncate">{t('stats.writingStreak')}</p>
            <div className="flex items-center gap-2.5">
              <p className="ritual-num whitespace-nowrap tabular-nums !text-[clamp(20px,1.8vw,30px)]">
                <CountUp value={stats.streak} duration={600} /><small> {t('stats.days')}</small>
              </p>
              <span className={cn('candle-mini', stats.todayWords === 0 && 'out')} aria-hidden>
                <span className="fl" /><span className="wk" /><span className="wx" />
              </span>
            </div>
          </div>
          <div className="dcard min-w-0">
            <p className="dcard-t truncate">{t('hud.bestNight', 'melhor noite')}</p>
            <p className="ritual-num whitespace-nowrap tabular-nums !text-[clamp(20px,1.8vw,30px)]">
              <CountUp value={stats.bestDay?.wordsWritten ?? 0} />
              <small> {t('stats.words', 'palavras')}</small>
            </p>
          </div>
          <div className="dcard min-w-0">
            <p className="dcard-t truncate">{t('stats.sessionsCount')}</p>
            <p className="ritual-num whitespace-nowrap tabular-nums !text-[clamp(20px,1.8vw,30px)]"><CountUp value={stats.totalSessions} duration={600} /></p>
          </div>
        </Rise>

        {/* ── Constelação | Progresso semanal — lado a lado, meio a meio ── */}
        <Rise i={2} className="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:flex-[1.1] md:min-h-0 shrink-0 md:shrink">
          <div className="dcard flex flex-col h-[230px] md:h-auto md:min-h-0 min-w-0">
            <p className="dcard-t shrink-0">{t('hud.constellation', 'Constelação da semana')}</p>
            <AutoConstellation days={constDays} />
            <p className="shrink-0 truncate whitespace-nowrap font-mono text-[9.5px] uppercase tracking-[0.14em] text-muted-foreground text-center mt-1.5">
              {t('hud.bestNight', 'melhor noite')}: {stats.bestDay && stats.bestDay.wordsWritten > 0
                ? `${new Date(stats.bestDay.date).toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')} · ${stats.bestDay.wordsWritten.toLocaleString()}`
                : '—'} · {t('hud.starsLitWeek', '{{n}} estrelas acesas esta semana', { n: litStars })}
            </p>
          </div>
          <div className="dcard flex flex-col h-[230px] md:h-auto md:min-h-0 min-w-0">
            <div className="mb-2 flex items-center justify-between gap-2 shrink-0">
              <p className="dcard-t !mb-0 truncate">{t('stats.weeklyProgress')}</p>
              <span className="shrink-0 whitespace-nowrap font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                {t('stats.inWords', 'Em palavras')}
              </span>
            </div>
            <div className="flex-1 min-h-0">
              <EChartsChart
                heightClass="h-full min-h-[120px]"
                option={buildWeeklyBarOption(
                  constDays.map((d) => ({
                    ...d,
                    dayName: d.dayName.replace('.', '').slice(0, 3).toUpperCase(),
                  })),
                  buildPalette(),
                  500
                )}
              />
            </div>
          </div>
        </Rise>

        {/* ── 3 colunas iguais: Ritmo · Distribuição · Livro em foco ── */}
        <Rise i={3} className="grid grid-cols-1 md:grid-cols-3 gap-3.5 md:flex-1 md:min-h-0 shrink-0 md:shrink">

          {/* Fidelidade de Ritmo — coluna cheia */}
          <div className="dcard flex flex-col md:min-h-0 md:overflow-hidden min-w-0">
            <p className="dcard-t shrink-0">{t('stats.consistencyTitle', 'Fidelidade de Ritmo')}</p>
            <div className="space-y-2 md:flex-1 md:min-h-0 md:overflow-y-auto pr-1 pb-6 [scrollbar-width:thin] [scrollbar-color:rgba(168,184,232,0.25)_transparent] [mask-image:linear-gradient(180deg,#000_calc(100%-28px),transparent)]">
              <ConsistencyRow
                icon={<Flame className="h-3.5 w-3.5" aria-hidden />}
                label={t('stats.writingStreak')}
                value={`${stats.streak} ${t('stats.days')}`}
                accent="primary"
              />
              <ConsistencyRow
                icon={<FileText className="h-3.5 w-3.5" aria-hidden />}
                label={t('stats.totalChapters')}
                value={stats.totalChapters.toString()}
                accent="accent"
              />
              <ConsistencyRow
                icon={<TrendingUp className="h-3.5 w-3.5" aria-hidden />}
                label={t('stats.avgWordsChapter')}
                value={stats.avgWordsPerChapter.toLocaleString()}
                accent="primary"
              />
              <ConsistencyRow
                icon={<Clock className="h-3.5 w-3.5" aria-hidden />}
                label={t('stats.totalTime')}
                value={formatTime(stats.totalTimeMinutes)}
                accent="accent"
              />
              <ConsistencyRow
                icon={<Clock className="h-3.5 w-3.5" aria-hidden />}
                label={t('stats.avgSessionLength')}
                value={formatTime(stats.avgSessionLength)}
                accent="primary"
              />
              <ConsistencyRow
                icon={<Target className="h-3.5 w-3.5" aria-hidden />}
                label={t('stats.todayWords')}
                value={stats.todayWords.toLocaleString()}
                accent="accent"
              />
            </div>
          </div>

          {/* Distribuição por capítulo — gráfico preso à altura do cartão */}
          <div className="dcard flex flex-col h-[260px] md:h-auto md:min-h-0 md:overflow-hidden min-w-0">
            <p className="dcard-t shrink-0 truncate">{t('stats.wordDistribution')}</p>
            {(() => {
              const chapterDist = (project.chapters || []).filter((ch) => ch.sectionType === 'chapter');
              if (chapterDist.length === 0) {
                return (
                  <div className="flex flex-1 flex-col items-center justify-center gap-3 py-8 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-muted/40">
                      <FileText className="h-6 w-6 text-muted-foreground/50" aria-hidden />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t('stats.noChaptersYet', 'Nenhum capítulo disponível ainda.')}
                    </p>
                  </div>
                );
              }
              const pal = buildPalette();
              return (
                <div className="min-h-0 flex-1 overflow-hidden">
                  <EChartsChart
                    heightClass="h-full"
                    option={{
                      grid: { left: 4, right: 30, top: 4, bottom: 4, containLabel: true },
                      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
                      xAxis: { type: 'value', show: false },
                      yAxis: {
                        type: 'category',
                        inverse: true,
                        data: chapterDist.map((c) => c.title),
                        axisTick: { show: false },
                        axisLine: { show: false },
                        axisLabel: { color: pal.mutedFg, fontSize: 10, width: 82, overflow: 'truncate' },
                      },
                      series: [
                        {
                          type: 'bar',
                          data: chapterDist.map((c) => c.wordCount),
                          barMaxWidth: 14,
                          itemStyle: { color: pal.primary, borderRadius: [0, 4, 4, 0] },
                          label: { show: true, position: 'right', color: pal.mutedFg, fontSize: 9, formatter: '{c}' },
                        },
                      ],
                    }}
                  />
                </div>
              );
            })()}
          </div>

          {/* Livro em foco — capa em cima, nível embaixo */}
          <div className="dcard flex flex-col items-center text-center md:min-h-0 md:overflow-hidden min-w-0">
            <p className="dcard-t shrink-0 self-stretch">{t('hud.currentBook', 'Livro em foco')}</p>
            <div className="flex-1 min-h-0 flex items-center justify-center py-1 w-full">
              <div className="h-full max-h-[190px] aspect-[5/7.4] drop-shadow-[0_22px_36px_rgba(0,0,0,0.55)]">
                <BookCover
                  title={project.metadata.title || t('projects.untitled', 'Sem título')}
                  author={project.metadata.author || ''}
                  genre={project.metadata.genre || 'Fiction'}
                  coverImage={project.metadata.coverImage}
                  variant="cover"
                  progress={Math.min(100, Math.round((stats.totalWords / 80000) * 100))}
                  seed={project.id}
                  disableTilt
                  className="w-full"
                />
              </div>
            </div>
            <b className="font-serif text-[14px] font-medium leading-tight text-foreground truncate max-w-full shrink-0">
              {project.metadata.title || t('projects.untitled', 'Sem título')}
            </b>
            <small className="mt-1 font-mono text-[8.5px] uppercase tracking-[0.16em] shrink-0 whitespace-nowrap" style={{ color: 'var(--ember)' }}>
              {t('hud.lvlAbbrev', 'Nv')} {writerLvl.level} · {writerTitleOf(writerLvl.level, t)}
            </small>
            <span className="xp-track mt-2 block w-full max-w-[180px] shrink-0">
              <span className="xp-fill block" style={{ width: `${writerLvl.pct}%` }} />
            </span>
            <small className="mt-1.5 font-mono text-[8px] uppercase tracking-[0.12em] text-muted-foreground shrink-0 whitespace-nowrap">
              {writerLvl.xpIn.toLocaleString()} / {LEVEL_XP.toLocaleString()} xp
            </small>
          </div>
        </Rise>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface MetricCardProps {
  label: string;
  value: string;
  /** When set, this is the streak tile — renders the animated mini candle,
      unlit (`out`) once the streak drops to zero. */
  streak?: number;
}

function MetricCard({ label, value, streak }: MetricCardProps) {
  return (
    <div className="dcard">
      <p className="dcard-t">{label}</p>
      <p className="font-serif text-xl md:text-2xl font-medium leading-none text-foreground truncate">
        {value}
      </p>
      {streak !== undefined && (
        <div className="mt-2.5 flex items-center gap-2">
          <span className={cn('candle-mini', streak === 0 && 'out')} aria-hidden>
            <span className="cm-f" />
            <span className="cm-w" />
            <span className="cm-x" />
          </span>
        </div>
      )}
    </div>
  );
}

interface ConsistencyRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent: 'primary' | 'accent';
}

function ConsistencyRow({ icon, label, value, accent }: ConsistencyRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-border/50 bg-muted/50 px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={cn(
            'shrink-0',
            accent === 'primary' ? 'text-primary' : 'text-accent'
          )}
        >
          {icon}
        </span>
        <span className="truncate text-xs text-muted-foreground">{label}</span>
      </div>
      <span
        className={cn(
          'shrink-0 font-mono text-xs font-semibold',
          accent === 'primary' ? 'text-primary' : 'text-accent'
        )}
      >
        {value}
      </span>
    </div>
  );
}
